import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";

export const updatePatrolAsync = createAsyncThunk(
  "patrols/updatePatrolAsync",
  async (data, {rejectWithValue}) => {
    try{
      const response = await httpClient.put(`/patrols/${data.id}`, data);
      if(response.status != 200) {
        throw new Error('Patrol update failed.');
      }
      return {
        ...data,
        success: response.status == 200 ? true : false,
        status: response.status
      };
    } catch(e) {
      return rejectWithValue({
        message: e.response?.data?.errors?.[0],
      });
    }
  } 
);

export const unassignTaskAsync = createAsyncThunk(
  "patrols/unassignTaskAsync",
  async (data, {rejectWithValue}) => {
    try{
      const response = await httpClient.post(`/tasks/${data.taskId}/deassign`);
      
      return {
        success: response.status == 200 ? true : false,
        taskId: data.taskId,
        patrolId: data.patrolId,
        status: response.status
      }
    } catch(e) {
      return rejectWithValue({
        message: e.response?.data?.errors?.[0],
      });
    }
  } 
);

export const assignTaskAsync = createAsyncThunk(
  "patrols/assignTaskAsync",
  async (data, {rejectWithValue}) => {
    try{
      const response = await httpClient.post(`/tasks/${data.taskId}/assign`, {patrolId: data.assignedPatrol});
      
      return {
        success: response.status == 200 ? true : false,
        taskId: data.taskId,
        patrolId: data.assignedPatrol,
        status: response.status
      }
    } catch(e) {
      return rejectWithValue({
        message: e.response?.data?.errors?.[0],
      });
    }
  } 
);

export const updateStatusAsync = createAsyncThunk(
  "patrols/updateStatus",
  async (data, {rejectWithValue}) => {
    let action;
    if(data.value == 'FREE') {
      action = 'signIn';
    }
    if(data.value == 'OFFD') {
      action = 'signOff';
    }
    try{
      const response = await httpClient.post(`/patrols/${data.entityId}/${action}`);
      
      return {
        success: response.status == 200 ? true : false,
        message: response.data.message,
        patrolId: data.entityId,
        status: data.value,
        impersonatedUser: data.impersonatedUser
      }
    } catch(e) {
      return rejectWithValue({
        message: e.response?.data?.errors?.[0],
      });
    }
  } 
);

export const setBreakAsync = createAsyncThunk(
  "patrols/setBreak",
  async (data, {rejectWithValue}) => {
    try{
      const response = await httpClient.post(`/patrols/${data.entityId}/break/${data.value}`);
      
      return {
        success: response.status == 200 ? true : false,
        message: response.data.message,
        patrolId: data.entityId,
        status: response.status,
      }
    } catch(e) {
      return rejectWithValue({
        message: e.response?.data?.errors?.[0],
      });
    }
  } 
);


export const suspendPatrolAsync = createAsyncThunk(
  "patrols/suspend",
  async ({patrolId, suspendUntil}, {rejectWithValue}) => {
    try{
      const response = await httpClient.post(`/patrols/${patrolId}/suspend`, {
        suspendUntil
      });
      if(response.status != 200) {
        throw new Error('Patrol suspend failed.');
      }
      return {
        ...response.data,
        patrolId,
        suspendUntil,
        success: response.status == 200 ? true : false
      };
      
    } catch(e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  } 
);

const patrolsSlice = createSlice({
  name: "patrols",
  initialState: {
    allPatrols: [],
    filteredPatrols: []
  },
  reducers: {
    setPatrols: (state, action) => { 
      //@TODO check if this is the best way & place to filter out OFFD patrols
      state.allPatrols = action.payload;
      state.filteredPatrols = action.payload;
    },
    filterPatrols: (state, action) => {
      const filters = action.payload.patrols;
      let patrols = state.allPatrols;

      if(action.payload.mixed && action.payload.mixed.resource.length) {
        const mixedFilters = action.payload.mixed.resource;
        if(mixedFilters.length) {
          patrols = patrols.filter(p => 
            (mixedFilters.includes(0) && p.resourceType.includes(1) ) ||
            (mixedFilters.includes(1) && p.resourceType.includes(2) ) ||
            (mixedFilters.includes(2) && p.resourceType.includes('N/A')) ||
            (mixedFilters.includes(3) && p.resourceType.includes('N/A'))
          );
        }
      }

      const comparisonMap = {
        fullName: (taskVal, filterVal) => {

          return taskVal?.toLowerCase().startsWith(filterVal?.toLowerCase())
        },
        status: (patrolVal, filterVal) => {
          if(filterVal.length) {

            return filterVal.includes(patrolVal.id);
          } else {
            return true;
          }
        },
        callsign: (patrolVal, filterVal) => {
          return patrolVal?.startsWith(filterVal)
        },
        default: (taskVal, filterVal) => 
          taskVal == filterVal
      }

      state.filteredPatrols = patrols.filter(patrol =>
        Object.entries(filters).every( ([key, value]) => {
          if(value === undefined) return true;

          const compare = comparisonMap[key] || comparisonMap.default;
          return compare(patrol[key], value);
        })
      );
    },
    assignTask: (state, action) => {
      const {patrolId, taskId} = action.payload;
      const patrol = state.allPatrols.find((p) => p.id == patrolId);
      const fpatrol = state.filteredPatrols.find((p) => p.id == patrolId);

      if(patrol) {
        if(!patrol.assignedTasks) {
          patrol.assignedTasks = [taskId]; //add array
        }
        if(!patrol.assignedTasks.includes(taskId)){
          patrol.assignedTasks.push(taskId);
        }
      }
      if(fpatrol) {
        if(!fpatrol.assignedTasks) {
          fpatrol.assignedTasks = [taskId]; //add array
        }
        if(!fpatrol.assignedTasks.includes(taskId)){
          fpatrol.assignedTasks.push(taskId);
        }
      }
    },
    unassignTask: (state, action) => {
      const {patrolId, taskId} = action.payload;
      const patrol = state.allPatrols.find((p) => p.id == patrolId);
      if(patrol) {
        const index = patrol.assignedTasks.indexOf(taskId);
        if(index > -1) {
          patrol.assignedTasks.splice(index,1);
        }
      }

      const fpatrol = state.filteredPatrols.find((p) => p.id == patrolId);

      if(fpatrol) {
        const findex = fpatrol.assignedTasks.indexOf(taskId);
        if(findex > -1) {
          fpatrol.assignedTasks.splice(findex,1);
        }
      }
    },
    updatePatrolPhone: (state, action) => {
      const { id, phoneNumber } = action.payload;
      const patrol = state.allPatrols.find((p) => p.id === id);
      if (patrol) {
        patrol.phoneNumber = phoneNumber;
      }
    },
    updateStatus: (state, action) => {
      const { entityId, value, impersonatedUser } = action.payload;
      const patrolId = impersonatedUser != undefined ? impersonatedUser : entityId
      const patrol = state.allPatrols.find((p) => p.id == patrolId);

      if (patrol) {
        patrol.status = value;
      }
    },
    updateLocation: (state, action) => {
      const { entityId, value } = action.payload;
      const patrol = state.allPatrols.find((p) => p.id === entityId);
      if (patrol) {
        patrol.location = {lat: value.lat, lng: value.lng};
      }
    },
    updatePatrol: (state, action) => {
      const { id } = action.payload;
      const patrol = state.allPatrols.find((p) => p.id === id);
      if (patrol) {
        Object.assign(patrol, action.payload);
      }
      const fpatrol = state.filteredPatrols.find((p) => p.id === id);
      if (fpatrol) {
        Object.assign(fpatrol, action.payload);
      }
    },
    batchUpdate: (state, action) => {
      console.log('BATCH P UPDATE', action.payload);
      const updatesById = new Map();
      for (const update of action.payload) {
        updatesById.set(update.id, update);
      }
     
      state.allPatrols = state.allPatrols.map(t =>
        updatesById.has(t.id) ? { ...t, ...updatesById.get(t.id) } : t
      );
     
      state.filteredPatrols = state.filteredPatrols.map(t =>
        updatesById.has(t.id) ? { ...t, ...updatesById.get(t.id) } : t
      );
    },
    addPatrol: (state, action) => {
      const { value } = action.payload;
      state.allPatrols.push(value);
      state.filteredPatrols.push(value);
    },
    assignTaskWS: (state, action) => {
      const { value } = action.payload;
      const patrol = state.allPatrols.find((p) => p.id === value.patrolId);
      if (patrol) {
        patrol.assignedTasks = [value.taskId];
      }
    },
    removeAssignment: (state, action) => {
      const {patrolCallsign, taskId} = action.payload;
      const index = state.allPatrols.findIndex((patrol) => patrol.callsign == patrolCallsign);
      if (index !== -1) {
        const updatedAssignments = state.allPatrols[index].assignedTasks.filter(a => a != taskId);
        state.allPatrols[index] = { ...state.allPatrols[index], assignments: updatedAssignments };
      }
    },
    showAssignments: (state, action) => {
      
      const { id } = action.payload;
     
      const patrol = state.allPatrols.find((p) => p.id == id);
      if (patrol) {
        patrol.showLines = patrol.showLines ? 0 : 1;
      }
    },
    testUpdate: (state, action) => {
      const { id, field, value } = action.payload;
      const index = state.allPatrols.findIndex((patrol) => patrol.id == id);
      if (index !== -1) {
        state.allPatrols[index][field] = value;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(unassignTaskAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unassignTaskAsync.fulfilled, (state) => {
        state.loading = false;
        state.error = null;

        // const resp = action.payload;
        // console.log('UNASSIGN TASK FROM FULLFILLED', resp);
        // if (resp.success) {
        //   const index = state.allPatrols.findIndex((patrol) => patrol.id == resp.patrolId);
        //   if (index !== -1) {
        //     const updatedAssignments = state.allPatrols[index].assignedTasks?.filter(a => a != resp.taskId);
        //     state.allPatrols[index] = { ...state.allPatrols[index], assignments: updatedAssignments };
        //   }
        //   console.log('P', state.allPatrols[index]);
        // }
        
        
      })
      .addCase(unassignTaskAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Patrol deassign failed';
      })


      .addCase(assignTaskAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignTaskAsync.fulfilled, (state) => {
        state.loading = false;
        state.error = null;

        // const resp = action.payload;
        // console.log('ASSIGN TASK FROM FULLFILLED', resp);
        // if (resp.success) {
        //   const index = state.allPatrols.findIndex((patrol) => patrol.id == resp.patrolId);
        //   if (index !== -1) {
        //     const initialAssignments = state.allPatrols[index].assignedTasks ? state.allPatrols[index].assignedTasks : [];
        //     const updatedAssignments = [...initialAssignments, resp.taskId];
        //     state.allPatrols[index] = { ...state.allPatrols[index], assignments: updatedAssignments };
        //   }
        //   console.log('P2', state.allPatrols[index], resp);
        // }
        
        
      })
      .addCase(assignTaskAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Task assigned.';
      })


      .addCase(updateStatusAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStatusAsync.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateStatusAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Update status failed.';
        return state;
      })


      .addCase(setBreakAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setBreakAsync.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(setBreakAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Update status failed.';
        return state;
      })


      .addCase(updatePatrolAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePatrolAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const resp = action.payload;
        if (resp?.success) {
          const { id, phoneNumber, type } = resp;
          
          const patrol = state.allPatrols.find((p) => p.id == id);

          if (patrol) {
            patrol.phoneNumber = phoneNumber;
            patrol.type = type;
          }
        }
        
        
      })
      .addCase(updatePatrolAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Update patrol failed.';
        return state;
      })
      
      .addCase(suspendPatrolAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(suspendPatrolAsync.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        // const response = action.payload;
        // if(response.success) {
        //   const patrol = state.allPatrols.find((p) => p.id == response.patrolId);
        //   patrol.status = 'UVOR';
        //   patrol.suspendUntil = response.suspendUntil;
        // }
      })
      .addCase(suspendPatrolAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Update suspend failed.';
        return state;
      })
  }
});

export const { setPatrols, updatePatrolPhone, filterPatrols } = patrolsSlice.actions;
export default patrolsSlice.reducer;
